/**
 * Login page with email/password form, MFA challenge, and OAuth provider buttons.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { coreApiMode, landingPath } from '@/config/deployment';
import { apiError } from '@/utils/apiError';
import { afterSignIn } from '@/utils/afterSignIn';
import {discoverSignIn,startSso,ssoProviderNames,type SsoProvider} from '@/api/identity';
import BrandTitle from '@/components/common/BrandTitle';
import BrandMark from '@/components/common/BrandMark';

type Step = 'email' | 'password' | 'sso' | 'redirect' | 'mfa';

export default function LoginPage() {
  const { login, verifyMfa, useRecoveryCode } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams]=useSearchParams();

  const [step, setStep] = useState<Step>(useAuthStore.getState().mfaChallenge?'mfa':'email');
  const [provider, setProvider] = useState<SsoProvider>('microsoft');
  const providerLabel = ssoProviderNames[provider];
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function continueWithSso() {
    setIsLoading(true);setError('');
    try { await startSso({ mode: 'login', email: email.trim() }); }
    catch (error) { setError(apiError(error)); setStep('sso'); setIsLoading(false); }
  }

  async function handleEmail(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!coreApiMode) { setStep('password'); return; }
    setIsLoading(true);
    try {
      const route = await discoverSignIn(email.trim());
      if (route.method === 'sso' && route.provider) {
        setProvider(route.provider);
        if (route.password_allowed) { setStep('sso'); setIsLoading(false); }
        else { setStep('redirect'); await continueWithSso(); }
      } else { setStep('password'); setIsLoading(false); }
    } catch (error) {
      // If routing is unavailable, fall back to the password form rather than blocking sign-in.
      setError(apiError(error)); setStep('password'); setIsLoading(false);
    }
  }

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const mfaRequired = await login(email, password);
      if(sessionStorage.getItem('enrollment_token')){navigate('/enroll');}
      else if (mfaRequired) {
        setStep('mfa');
      } else {
        navigate(afterSignIn(landingPath));
      }
    } catch (error) {
      setError(coreApiMode ? apiError(error) : 'Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMfaVerify(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (showRecovery) {
        await useRecoveryCode(recoveryCode.trim());
      } else {
        await verifyMfa(mfaCode.trim());
      }
      navigate(afterSignIn(landingPath));
    } catch {
      setError(showRecovery ? 'Invalid recovery code.' : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Brand */}
        <div className="text-center">
          <BrandMark className="mx-auto h-14 w-14" />
          <BrandTitle/>
          <p className="mt-1 text-sm text-gray-500">Continuous vulnerability management</p>
        </div>

        {searchParams.get('reason')==='session_expired'&&<p role="status" className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">Your session has expired. Please sign in again.</p>}
        {error && step !== 'mfa' && (
          <div role="alert" className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Email step: decide between the organization's SSO provider and a password */}
        {step === 'email' && (
          <form onSubmit={handleEmail} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input id="email" type="email" className="input-field mt-1" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" autoComplete="username" required autoFocus />
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary w-full">{isLoading ? 'Checking...' : 'Continue'}</button>
          </form>
        )}

        {step !== 'email' && step !== 'mfa' && (
          <div className="flex items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm">
            <span className="truncate text-gray-700">{email}</span>
            <button type="button" className="text-indigo-600 hover:text-indigo-800" onClick={() => { setStep('email'); setPassword(''); setError(''); }}>Change</button>
          </div>
        )}

        {step === 'redirect' && <p role="status" className="text-center text-sm text-gray-600">Redirecting to {providerLabel} sign-in…</p>}

        {step === 'sso' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Your organization signs in with {providerLabel}.</p>
            <button type="button" className="btn-primary w-full" disabled={isLoading} onClick={() => void continueWithSso()}>Continue with {providerLabel}</button>
            <button type="button" className="w-full text-sm text-indigo-600 hover:text-indigo-800" onClick={() => { setStep('password'); setError(''); }}>Use password instead</button>
          </div>
        )}

        {step === 'password' && (
          <form onSubmit={handleCredentials} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input id="password" type="password" className="input-field mt-1" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password" autoComplete="current-password" required autoFocus />
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary w-full">{isLoading ? 'Signing in...' : 'Sign In'}</button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-800">Forgot password?</Link>
            </div>
          </form>
        )}

        {/* MFA challenge step */}
        {step === 'mfa' && (
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div className="rounded-md bg-indigo-50 px-4 py-3 text-center">
              <p className="text-sm font-medium text-indigo-800">Two-factor authentication required</p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!showRecovery ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Authentication Code</label>
                  <input
                    className="input-field mt-1 text-center text-lg tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the 6-digit code from your authenticator app.
                  </p>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowRecovery(true); setError(''); }}
                  className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800"
                >
                  Use a recovery code instead
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Recovery Code</label>
                  <input
                    className="input-field mt-1 text-center font-mono uppercase"
                    placeholder="XXXXXXXX"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    autoFocus
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter one of your recovery codes.
                  </p>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? 'Verifying...' : 'Use Recovery Code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowRecovery(false); setError(''); }}
                  className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800"
                >
                  Use authenticator code instead
                </button>
              </>
            )}

            <button
              type="button"
              onClick={() => { setStep('email'); setError(''); }}
              className="w-full text-center text-xs text-gray-500 hover:text-gray-700"
            >
              Back to login
            </button>
          </form>
        )}
        {coreApiMode&&<p className="border-t pt-4 text-center text-xs text-gray-500">
          Use of Vantage is subject to the <Link className="text-indigo-600 hover:text-indigo-800" to="/legal/terms">Terms of Service</Link> and the{' '}
          <Link className="text-indigo-600 hover:text-indigo-800" to="/legal/acceptable-use">Acceptable Use Policy</Link>. See our{' '}
          <Link className="text-indigo-600 hover:text-indigo-800" to="/legal/privacy">Privacy Policy</Link>.
        </p>}
      </div>
    </div>
  );
}
